import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, addDoc, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import './SuperAdminNotificationsView.css';
import { schoolMatches } from '../utils/schoolScope';

// Mirrors iOS SuperAdminNotificationsViewModel.RecipientType raw values.
const RECIPIENT_TYPES = [
  'All Parents',
  'All Teachers',
  'All Admins',
  'All Users',
  'School Parents',
  'School Teachers',
  'School Admins',
  'Entire School',
  'Specific User'
];
const SCHOOL_SCOPED_RECIPIENTS = ['School Parents', 'School Teachers', 'School Admins', 'Entire School'];

// iOS NotificationType raw values (Notification.swift).
const NOTIFICATION_TYPES = [
  { value: 'general', label: 'General' },
  { value: 'national', label: 'National' },
  { value: 'school', label: 'School' },
  { value: 'new_event', label: 'New Event' }
];

function SuperAdminNotificationsView() {
  const [notifications, setNotifications] = useState([]);
  const [schools, setSchools] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [filterSchool, setFilterSchool] = useState('all');

  const [newNotification, setNewNotification] = useState({
    title: '',
    message: '',
    type: 'general',
    recipientType: 'All Parents',
    schoolId: '',
    targetUserId: ''
  });

  const loadNotifications = useCallback(async () => {
    try {
      // Load all then filter in memory so schoolId matches case-insensitively
      const snapshot = await getDocs(collection(db, 'notifications'));
      let notificationsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      if (filterSchool !== 'all') {
        notificationsData = notificationsData.filter(n => schoolMatches(n.schoolId, filterSchool));
      }

      // Sort by createdAt descending
      notificationsData.sort((a, b) => {
        const timeA = a.createdAt?.toDate?.() || new Date(a.createdAt);
        const timeB = b.createdAt?.toDate?.() || new Date(b.createdAt);
        return timeB - timeA;
      });

      setNotifications(notificationsData);
    } catch (error) {
      console.error('🔥 Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [filterSchool]);

  useEffect(() => {
    loadNotifications();
    getDocs(collection(db, 'schools'))
      .then(snap => setSchools(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(() => {});
    // Users for "Specific User" targeting (iOS loads the `users` collection).
    getDocs(collection(db, 'users'))
      .then(snap => setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(() => {});
  }, [loadNotifications]);

  const getSchoolName = (schoolId) => {
    if (!schoolId || schoolId === 'all') return 'All Schools (National)';
    const school = schools.find(s => schoolMatches(s.id, schoolId) || schoolMatches(s.uuid, schoolId));
    return school?.name || schoolId;
  };

  const requiresSchool = SCHOOL_SCOPED_RECIPIENTS.includes(newNotification.recipientType);
  const requiresUser = newNotification.recipientType === 'Specific User';

  const handleAddNotification = async (e) => {
    e.preventDefault();
    try {
      // iOS-compatible AppNotification fields: UUID id, ISO createdAt,
      // snake_case type raw values, UUID schoolId/parentId (or absent/null).
      const data = {
        id: crypto.randomUUID(),
        title: newNotification.title,
        message: newNotification.message,
        type: newNotification.type,
        isRead: false,
        isActive: true,
        createdAt: new Date().toISOString(),
        recipientType: newNotification.recipientType,
        schoolId: requiresSchool && newNotification.schoolId ? newNotification.schoolId : null
      };

      if (requiresUser && newNotification.targetUserId) {
        const target = users.find(u => u.id === newNotification.targetUserId);
        // iOS user listeners query `parentId`/`studentId` by UUID — use the
        // user's uuid field when present so the notification actually arrives.
        const targetUuid = target?.uuid || target?.id || newNotification.targetUserId;
        data.targetUserId = targetUuid;
        data.parentId = targetUuid;
      }

      console.log('🔥 SuperAdminNotifications: sending notification', data);
      await addDoc(collection(db, 'notifications'), data);
      setNewNotification({ title: '', message: '', type: 'general', recipientType: 'All Parents', schoolId: '', targetUserId: '' });
      setShowAddForm(false);
      loadNotifications();
    } catch (error) {
      console.error('🔥 Error adding notification:', error);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    if (window.confirm('Are you sure you want to delete this notification?')) {
      try {
        await deleteDoc(doc(db, 'notifications', notificationId));
        loadNotifications();
      } catch (error) {
        console.error('Error deleting notification:', error);
      }
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'national': return '🏛️';
      case 'school': return '🏫';
      case 'new_event': return '📅';
      case 'newEvent': return '📅';
      default: return 'ℹ️';
    }
  };

  if (loading) {
    return <div className="loading-container">Loading notifications...</div>;
  }

  return (
    <div className="super-admin-notifications-view">
      <div className="view-header">
        <h2>Notifications Management</h2>
        <button 
          className="add-button"
          onClick={() => setShowAddForm(true)}
        >
          + Add Notification
        </button>
      </div>

      <div className="filters-bar">
        <div className="filter-group">
          <label>School:</label>
          <select value={filterSchool} onChange={(e) => setFilterSchool(e.target.value)}>
            <option value="all">All Schools</option>
            {schools.map(school => (
              <option key={school.id} value={school.id}>{school.name || school.id}</option>
            ))}
          </select>
        </div>
      </div>

      {showAddForm && (
        <div className="add-form-card">
          <h3>Add New Notification</h3>
          <form onSubmit={handleAddNotification}>
            <div className="form-group">
              <label>Title</label>
              <input
                type="text"
                value={newNotification.title}
                onChange={(e) => setNewNotification({ ...newNotification, title: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Message</label>
              <textarea
                value={newNotification.message}
                onChange={(e) => setNewNotification({ ...newNotification, message: e.target.value })}
                rows={4}
                required
              />
            </div>
            <div className="form-group">
              <label>Type</label>
              <select
                value={newNotification.type}
                onChange={(e) => setNewNotification({ ...newNotification, type: e.target.value })}
              >
                {NOTIFICATION_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Audience</label>
              <select
                value={newNotification.recipientType}
                onChange={(e) => setNewNotification({ ...newNotification, recipientType: e.target.value })}
              >
                {RECIPIENT_TYPES.map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            {requiresSchool && (
              <div className="form-group">
                <label>School</label>
                <select
                  value={newNotification.schoolId}
                  onChange={(e) => setNewNotification({ ...newNotification, schoolId: e.target.value })}
                  required
                >
                  <option value="">Select a school</option>
                  {schools.map(school => (
                    <option key={school.id} value={school.id}>{school.name || school.id}</option>
                  ))}
                </select>
              </div>
            )}
            {requiresUser && (
              <div className="form-group">
                <label>User</label>
                <select
                  value={newNotification.targetUserId}
                  onChange={(e) => setNewNotification({ ...newNotification, targetUserId: e.target.value })}
                  required
                >
                  <option value="">Select a user</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>
                      {(u.displayName || u.username || u.email || u.id)} {u.role ? `(${u.role})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="form-actions">
              <button type="button" onClick={() => setShowAddForm(false)}>Cancel</button>
              <button type="submit">Add Notification</button>
            </div>
          </form>
        </div>
      )}

      <div className="notifications-container">
        {notifications.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🔔</div>
            <h3>No notifications found</h3>
            <p>Create notifications to keep users informed</p>
          </div>
        ) : (
          <div className="notifications-list">
            {notifications.map(notification => (
              <div key={notification.id} className="notification-card">
                <div className="notification-icon">
                  {getNotificationIcon(notification.type)}
                </div>
                <div className="notification-content">
                  <div className="notification-header">
                    <h3>{notification.title}</h3>
                    <span className="notification-type">{notification.type}</span>
                  </div>
                  <p className="notification-message">{notification.message}</p>
                  {notification.recipientType && (
                    <p className="notification-audience" style={{ fontSize: '0.85rem', color: '#475569', margin: '2px 0' }}>👥 {notification.recipientType}</p>
                  )}
                  <p className="notification-school" style={{ fontSize: '0.85rem', color: '#64748b', margin: '4px 0' }}>🏫 {getSchoolName(notification.schoolId)}</p>
                  <div className="notification-footer">
                    <span className="notification-time">{formatTimestamp(notification.createdAt)}</span>
                    <button 
                      className="delete-button"
                      onClick={() => handleDeleteNotification(notification.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default SuperAdminNotificationsView;
